import React, { useState, useEffect, useCallback } from 'react';
import { signOut, getCurrentUser, fetchUserAttributes, fetchAuthSession } from 'aws-amplify/auth';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import '../css/Dashboard.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const storeSub = (sub) => {
  try {
    localStorage.setItem('userSub', sub);
  } catch (error) {
    console.error('Error storing sub:', error);
  }
};

const getStoredSub = () => {
  try {
    return localStorage.getItem('userSub');
  } catch (error) {
    console.error('Error retrieving sub:', error);
    return null;
  }
};

const SimpleCardForm = ({ onSubscriptionSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setSuccess(false);
    setIsLoading(true);

    try {
      const { tokens } = await fetchAuthSession();
      const idToken = tokens.idToken.toString();
      const storedSub = getStoredSub();

      if (!storedSub) {
        throw new Error('No user sub found. Please sign in again.');
      }

      if (!stripe || !elements) {
        throw new Error('Stripe has not loaded yet. Please try again.');
      }

      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error('Card element not found. Please try again.');
      }

      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name,
          email,
        },
      });

      if (pmError) {
        setError(pmError.message);
        setIsLoading(false);
        return;
      }

      const response = await fetch(process.env.REACT_APP_EQUATION_LIVE_STRIPE_USER_CREATION, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`, 
        },
        body: JSON.stringify({
          email,
          name,
          sub: storedSub,
          payment_method: paymentMethod.id,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        if (response.status === 400 && errorData.message && errorData.message.includes("already has an active subscription")) {
          setError("You already have an active subscription! No additional payment is needed. If you're having issues accessing your subscription, please contact support.");
        } else {
          setError(errorData.message || `HTTP error! status: ${response.status}`);
        }
        setIsLoading(false);
        return;
      }

      const data = await response.json();
      setSuccess(true);
      setIsLoading(false);
      setName('');
      setEmail('');
      cardElement.clear();
      setTimeout(() => {
        if (onSubscriptionSuccess) {
          onSubscriptionSuccess();
        }
        onSubscriptionSuccess();
      }, 2000);

    } catch (err) {
      console.error('Error:', err);
      setError(err.message || 'An error occurred');
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ maxWidth: '400px', margin: '0 auto' }}>
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '8px' }}>Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          style={{
            width: '100%',
            padding: '10px',
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            marginBottom: '16px'
          }}
        />
        <label style={{ display: 'block', marginBottom: '8px' }}>Email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          style={{
            width: '100%',
            padding: '10px',
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            marginBottom: '16px'
          }}
        />
      </div>
      <div style={{ padding: '20px', border: '1px solid #e0e0e0', borderRadius: '8px', marginBottom: '20px', backgroundColor: 'white' }}>
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
            },
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9em', color: '#636e72', marginTop: '8px' }}>
        </div>
      </div>
      <button
        type="submit"
        disabled={!stripe || isLoading}
        style={{
          background: '#19c37d',
          color: 'white',
          padding: '15px 30px',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer',
          width: '100%',
          marginBottom: '20px',
          opacity: isLoading ? 0.7 : 1,
          fontSize: '16px',
          fontWeight: '600'
        }}
      >
        {isLoading ? 'Processing...' : 'Subscribe Now'}
      </button>
      <div 
        style={{
          padding: '15px',
          borderRadius: '4px',
          marginTop: '10px',
          textAlign: 'center',
          display: error || success || isLoading ? 'block' : 'none',
          backgroundColor: error ? '#ffebee' : success ? '#e8f5e9' : '#f5f5f5',
          color: error ? '#c62828' : success ? '#2e7d32' : '#424242',
          border: `1px solid ${error ? '#ffcdd2' : success ? '#c8e6c9' : '#e0e0e0'}`
        }}
      >
        {isLoading && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
            <div style={{
              width: '20px',
              height: '20px',
              border: '2px solid #f3f3f3',
              borderTop: '2px solid #19c37d',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <span>Processing your subscription...</span>
          </div>
        )}
        {error && !isLoading && <div>{error}</div>}
        {success && !isLoading && <div>Subscription created successfully!</div>}
      </div>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </form>
  );
};

async function fetchUserData({ email, token }) {
  try {
    if (!token) {
      throw new Error('No authentication token found. Please sign in again.');
    }
    const response = await fetch(process.env.REACT_APP_EQUATION_LIVE_WEBSITE_SIGN_IN, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        email: email
      })
    });
    if (!response.ok) {
      const responseText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(responseText);
      } catch (e) {
        errorData = { message: responseText };
      }
      console.error('Error response data:', errorData);
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    if (data.sub) {
      storeSub(data.sub);
    }
    return data;
  } catch (error) {
    console.error('Error in fetchUserData:', error);
    throw error;
  }
}

const Dashboard = ({ user, onAuthStateChange }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [userData, setUserData] = useState({
    isLoading: true,
    data: null,
    error: null
  });
  const [isCancelling, setIsCancelling] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const loadUserData = useCallback(async () => {
    try {
      const attributes = await fetchUserAttributes();
  
      if (attributes.sub) {
        storeSub(attributes.sub);
      }
    
      const { tokens } = await fetchAuthSession();
      const idToken = tokens.idToken.toString();
      const data = await fetchUserData({ 
        email: attributes.email,
        token: idToken
      });
      
      const userDataObj = data.user;
      const processedData = {
        email: userDataObj.email,
        enable_extension: userDataObj.enable_extension,
        paymentStatus: userDataObj.paymentStatus,
        subscriptionPlan: userDataObj.subscriptionPlan,
        signupDate: userDataObj.signupDate,
        lastPaidDate: userDataObj.lastPaidDate,
        nextPaymentDate: userDataObj.nextPaymentDate,
        stripeCustomerId: userDataObj.stripeCustomerId,
        userId: userDataObj.UserID
      };
      setUserData({
        isLoading: false,
        data: processedData,
        error: null
      });
    } catch (error) {
      console.error("Error loading user data:", error);
      setUserData({
        isLoading: false,
        data: null,
        error: error.message || "Failed to load user data"
      });
      if (error.message.includes('auth') || error.message.includes('unauthorized')) {
        handleSignOut();
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  const handleSignOut = async () => {
    try {
      await signOut();
      
      setUserData({
        isLoading: false,
        data: null,
        error: null
      });
      
      window.location.href = '/';
    } catch (err) {
      console.error('Sign out error:', err);
      window.location.href = '/';
    }
  };

  const handleCancelSubscription = async () => {
    setIsCancelling(true);
    try {
      const { tokens } = await fetchAuthSession();
      const idToken = tokens.idToken.toString();
      const storedSub = getStoredSub();

      if (!userData.data.stripeCustomerId || userData.data.stripeCustomerId === 'null') {
        throw new Error('No valid subscription found to cancel');
      }

      const customerId = userData.data.stripeCustomerId;

      const response = await fetch(process.env.REACT_APP_EQUATION_LIVE_STRIPE_USER_SUBSCRIPTION_DELETE, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,  
        },
        body: JSON.stringify({
          customerId,
          sub: storedSub, 
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to cancel subscription');
      }
      
      const data = await response.json();
    
      setSuccessMessage('Your subscription has been cancelled successfully.');
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      setSuccessMessage(error.message || 'Failed to cancel subscription. Please try again later.');
      setShowSuccessModal(true);
    } finally {
      setIsCancelling(false);
      setShowCancelModal(false);
    }
  };

  const openCancelModal = () => {
    setShowCancelModal(true);
  };

  const closeCancelModal = () => {
    setShowCancelModal(false);
  };

  const closeSuccessModal = async () => {
    setShowSuccessModal(false);
    setSuccessMessage('');
    
    if (successMessage.includes('cancelled successfully')) {
      setUserData(prev => ({ ...prev, isLoading: true }));
      await new Promise(resolve => setTimeout(resolve, 1000));
      await loadUserData();
    }
  };

  if (isLoading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (userData.error) {
    return (
      <div className="dashboard-error">
        <h2>Error</h2>
        <p>{userData.error}</p>
        <button onClick={handleSignOut}>Sign Out</button>
      </div>
    );
  }

  if (!userData.data) {
    handleSignOut();
    return null;
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  };

  const getPlanName = (plan) => {
    const planMap = {
      basic: 'Basic',
      comped: 'Comped',
      trial: 'Trial',
      unsubscribed: 'Unsubscribed'
    };
    const result = planMap[plan?.toLowerCase()] || 'Unsubscribed';
    return result;
  };

  return (
    <div className="dashboard">
      <nav className="dashboard-nav">
        <div className="nav-brand">
          <h1>Equation Live</h1>
        </div>
        <div className="nav-actions">
          <span className="user-email">{userData.data.email}</span>
          <button className="sign-out-button" onClick={handleSignOut}>
            Sign Out
          </button>
        </div>
      </nav>

      <main className="dashboard-content">
        <section className="dashboard-hero">
          <h2>Welcome to Equation Live</h2>
        </section>

        <section className="dashboard-subscription">
          <div className="subscription-content">
            <div className="subscription-info">
              <div className="user-info-section">
                <h3>Account Information</h3>
                <div className="user-details">
                  <div className="user-email">
                    <span className="label">Email:</span>
                    <span className="value">{userData.data.email}</span>
                  </div>
                  <div className="user-signup">
                    <span className="label">Signup Date:</span>
                    <span className="value">{formatDate(userData.data.signupDate)}</span>
                  </div>
                  {userData.data.lastPaidDate && (
                    <div className="user-signup">
                      <span className="label">Last Payment:</span>
                      <span className="value">{formatDate(userData.data.lastPaidDate)}</span>
                    </div>
                  )}
                  {userData.data.nextPaymentDate && (
                    <div className="user-signup">
                      <span className="label">Next Payment:</span>
                      <span className="value">{formatDate(userData.data.nextPaymentDate)}</span>
                    </div>
                  )}
                  <div className="account-actions">
                    <button 
                      className="action-button cancel-subscription"
                      onClick={openCancelModal}
                      disabled={isCancelling || !userData.data.stripeCustomerId || userData.data.stripeCustomerId === 'null'}
                    >
                      {isCancelling ? 'Cancelling...' : 'Cancel Subscription'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="plan-info-section">
                <h3>Current Plan</h3>
                {userData.isLoading ? (
                  <div className="plan-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading plan details...</p>
                  </div>
                ) : userData.error ? (
                  <div className="plan-error">
                    <p>{userData.error}</p>
                  </div>
                ) : (
                  <>
                    <div className="plan-status">
                      <span className="status-badge">{getPlanName(userData.data.subscriptionPlan)}</span>
                      <span className="price">
                        {userData.data.paymentStatus === 'active' 
                            ? 'Active Subscription' 
                            : 'No Active Subscription'}
                      </span>
                    </div>
                    <ul className="plan-features">
                      <li>Extension Access: {userData.data.enable_extension ? 'Enabled' : 'Disabled'}</li>
                      <li>Stripe Customer: {userData.data.stripeCustomerId === 'null' ? 'Not Linked' : 'Linked'}</li>
                      <li>Billed: {userData.data.paymentStatus === 'active' ? 'monthly' : 'Not Subscribed'}</li>
                    </ul>
                  </>
                )}
              </div>
            </div>

            <div className="subscription-payment">
              <h3>Payment Details</h3>
              
              <div className="subscription-plan-info">
                <h4>Subscribe to Basic Plan</h4>
                <div className="plan-details">
                  <div className="plan-header">
                    <div className="plan-name">Basic Plan</div>
                    <div className="plan-price">$4.99<span className="price-period">/month</span></div>
                  </div>
                  <div className="plan-description">
                    <p>Get full access to Equation Live with our Basic subscription plan.</p>
                    <ul className="plan-benefits">
                      <li>Full Keyboard Support</li>
                      <li>1 Month Free Trial</li>
                      <li>Monthly Billing</li>
                    </ul>
                  </div>
                </div>
              </div>

              {stripePromise ? (
                <Elements stripe={stripePromise}>
                  <SimpleCardForm onSubscriptionSuccess={loadUserData} />
                </Elements>
              ) : (
                <div style={{ 
                  padding: '20px', 
                  textAlign: 'center', 
                  color: '#666',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0'
                }}>
                  <p>Payment processing is currently unavailable. Please check your configuration.</p>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>Equation Live</h4>
            <p>Making LaTeX editing and rendering easy in ChatGPT</p>
          </div>
          <div className="footer-section">
            <ul>
              <li><a href="/support">Support</a></li>
              <li><a href="/about">About</a></li>
              <li><a href="/privacy">Privacy Policy</a></li>
              <li><a href="/terms">Terms of Service</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} Equation Live. All rights reserved.</p>
        </div>
      </footer>

      {showCancelModal && (
        <div className="modal-overlay" onClick={closeCancelModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Cancel Subscription</h3>
              <button className="modal-close" onClick={closeCancelModal}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="warning-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                  <line x1="12" y1="9" x2="12" y2="13"></line>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
              </div>
              <h4>Are you sure you want to cancel?</h4>
              <p>This action will cancel your current subscription. You will immediately lose access to Equation Live.</p>
              <div className="modal-details">
                <div className="detail-item">
                  <span className="detail-label">Current Plan:</span>
                  <span className="detail-value">{getPlanName(userData.data.subscriptionPlan)}</span>
                </div>
                {userData.data.nextPaymentDate && (
                  <div className="detail-item">
                    <span className="detail-label">Next Payment:</span>
                    <span className="detail-value">{formatDate(userData.data.nextPaymentDate)}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="modal-button secondary" 
                onClick={closeCancelModal}
                disabled={isCancelling}
              >
                Keep Subscription
              </button>
              <button 
                className="modal-button danger" 
                onClick={handleCancelSubscription}
                disabled={isCancelling}
              >
                {isCancelling ? (
                  <>
                    <div className="spinner"></div>
                    Cancelling...
                  </>
                ) : (
                  'Yes, Cancel Subscription'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuccessModal && (
        <div className="modal-overlay" onClick={closeSuccessModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Subscription Cancelled</h3>
              <button className="modal-close" onClick={closeSuccessModal}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="success-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22,4 12,14.01 9,11.01"></polyline>
                </svg>
              </div>
              <h4>{successMessage}</h4>
            </div>
            <div className="modal-footer">
              <button 
                className="modal-button primary" 
                onClick={closeSuccessModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
